<template>
  <div>
    <!-- shadow box on changesPending-->
    <div
      class="bg-black fixed top-0 left-0 h-screen w-screen z-10 opacity-50"
      v-if="changesPending"
    ></div>

    <!-- Modals -->
    <InfoautoCustomerFinder
      @selectedCustomer="selectedInfoautoCustomer"
      v-if="showInfoautoSearch"
      @close="showInfoautoSearch = false"
    ></InfoautoCustomerFinder>
    <InfoautoCustomerFinder
      @selectedCustomer="selectedReceiver"
      v-if="showReceiverSearch"
      @close="showReceiverSearch = false"
    ></InfoautoCustomerFinder>
    <InfoautoAddressForm
      class="z-50"
      v-if="showNewInfoautoAddressForm && !isCostaRicaStore"
      :customerName="forwarderCustomerName"
      :customerInfoautoId="currentInfoautoCustomerId"
      @close="showNewInfoautoAddressForm = false"
      @addressCreated="handleForwarderAddressCreated"
    ></InfoautoAddressForm>
    <InfoautoAddressFormCostaRica
      class="z-50"
      v-if="showNewInfoautoAddressForm && isCostaRicaStore"
      :customerName="forwarderCustomerName"
      :customerInfoautoId="currentInfoautoCustomerId"
      @close="showNewInfoautoAddressForm = false"
      @addressCreated="handleForwarderAddressCreated"
    ></InfoautoAddressFormCostaRica>
    <InfoautoPhoneForm
      class="z-50"
      v-if="showNewInfoautoPhoneForm"
      @close="showNewInfoautoPhoneForm = false"
      :customerName="forwarderCustomerName"
      :customerInfoautoId="currentInfoautoCustomerId"
      @phoneCreated="handleForwarderPhoneCreated"
    ></InfoautoPhoneForm>
    <InfoautoCustomerAddressPhonesModal
      v-if="showAddressPhoneModal"
      :customerId="sectionInfoautoId"
      :selectable="true"
      :currentAddress="address"
      @close="closeAddressPhoneModal"
      @address-selected="handleInfoautoAddressSelected"
      @phone-selected="handleInfoautoPhoneSelected"
    ></InfoautoCustomerAddressPhonesModal>
    <InfoautoEditAddressModal
      v-if="showEditInfoautoAddressModal && !isCostaRicaStore"
      :customerId="sectionInfoautoId"
      :address="selectedInfoautoAddress"
      @address-updated="handleInfoautoAddressUpdated"
      @close="showEditInfoautoAddressModal = false"
    ></InfoautoEditAddressModal>
    <InfoautoEditAddressModalCostaRica
      v-if="showEditInfoautoAddressModal && isCostaRicaStore"
      :customerId="sectionInfoautoId"
      :address="selectedInfoautoAddress"
      @address-updated="handleInfoautoAddressUpdated"
      @close="showEditInfoautoAddressModal = false"
    ></InfoautoEditAddressModalCostaRica>
    <WebAddressPhoneSelectorModal
      v-if="showWebAddressSelector"
      :customer="isShippingToReceiver ? null : customer"
      :customerInfoauto="sectionInfoauto"
      :currentAddress="address"
      selection-mode="address-only"
      @close="showWebAddressSelector = false"
      @address-selected="handleWebAddressSelected"
    ></WebAddressPhoneSelectorModal>
    <WebAddressPhoneSelectorModal
      v-if="showWebPhoneSelector"
      :customer="isShippingToReceiver ? null : customer"
      :customerInfoauto="sectionInfoauto"
      :currentAddress="address"
      selection-mode="phone-only"
      @close="showWebPhoneSelector = false"
      @phone-selected="handleWebPhoneSelected"
    ></WebAddressPhoneSelectorModal>

    <!-- Main Content -->
    <div class="box w3" :class="{ 'is-editing': changesPending }">
      <div class="box-header">
        <h6 class="title">
          {{ $t("message.order.address") }}
        </h6>
      </div>
      <div class="box-content-block">
        <!-- Tab Navigation -->
        <div class="tab-nav">
          <button
            class="tab-button"
            :class="{ 'is-active': addressSection === 'shipping-address' }"
            @click="$emit('changedSection', 'shipping-address')"
            :disabled="changesPending"
          >
            {{ $t("message.order.shipping_address") }}
          </button>
          <button
            class="tab-button"
            :class="{ 'is-active': addressSection === 'billing-address' }"
            @click="$emit('changedSection', 'billing-address')"
            :disabled="changesPending"
          >
            {{ $t("message.order.billing_address") }}
          </button>
          <button
            class="tab-button"
            :class="{ 'is-active': addressSection === 'forwarder-address' }"
            @click="$emit('changedSection', 'forwarder-address')"
            :disabled="changesPending"
          >
            {{ $t("message.order.forwarder_address") }}
          </button>
        </div>

        <!-- Same as Shipping Checkbox (Billing only) -->
        <div
          v-if="canBeEdited && addressSection === 'billing-address'"
          class="flex flex-row mb-3"
        >
          <template v-if="shippingInfoautoId">
            <p class="text-sm text-gray-600">
              {{ $t("message.order.receiver_billing_hint") }}
            </p>
          </template>
          <template v-else>
            <input
              type="checkbox"
              id="sameShippingAndBilling"
              :checked="sameShippingAndBilling"
              @input="
                $emit('updatedSameShippingAndBilling', $event.target.checked)
              "
              class="mr-2"
            />
            <label for="sameShippingAndBilling">{{
              $t("message.order.same_as_shipping")
            }}</label>
          </template>
        </div>

        <!-- Who receives (Shipping only): the customer or another Infoauto
             person; with another one the address and phone are hers -->
        <div
          v-if="addressSection === 'shipping-address' && customerInfoauto"
          class="receiver-row"
        >
          <span class="receiver-label">
            {{ $t("message.order.receiver") }}:
          </span>
          <span class="receiver-name">
            {{ shippingInfoautoId ? receiverName : customerDisplayName }}
          </span>
          <span v-if="shippingInfoautoId" class="receiver-badge">
            {{ $t("message.order.receiver_other_person") }}
          </span>
          <template v-if="canBeEdited">
            <button
              class="text-link"
              :disabled="saving || changesPending"
              @click="showReceiverSearch = true"
            >
              <i class="mdi mdi-account-switch"></i>
              {{
                shippingInfoautoId
                  ? $t("message.order.change_receiver")
                  : $t("message.order.ship_to_other_person")
              }}
            </button>
            <button
              v-if="shippingInfoautoId"
              class="text-link"
              :disabled="saving || changesPending"
              @click="saveReceiver(null)"
            >
              {{ $t("message.order.ship_to_customer") }}
            </button>
          </template>
        </div>

        <!-- Forwarder Section -->
        <ForwarderManager
          v-if="addressSection === 'forwarder-address'"
          :forwarder-infoauto-id="currentInfoautoCustomerId"
          :forwarder-name="forwarderCustomerName"
          :address-options="forwarderAddressOptions"
          :phone-options="forwarderPhoneOptions"
          :address="forwarderAddress"
          :phone="forwarderPhone"
          :loading="loading"
          :saving="saving"
          :can-edit="canBeEdited"
          :can-save-address-default="canSaveForwarderAddressDefault"
          :can-save-customer-forwarder="canSaveCustomerForwarder"
          @save-default="saveDefault"
          @select-forwarder="showInfoautoSearch = true"
          @use-order-customer="sameCustomerInOrder"
          @remove-forwarder="removeForwarder"
          @create-address="showNewInfoautoAddressForm = true"
          @create-phone="showNewInfoautoPhoneForm = true"
          @save="saveForwarderChanges"
          ref="forwarderManager"
        ></ForwarderManager>

        <!-- Shipping/Billing Address Section -->
        <AddressEditorForm
          v-else
          ref="addressEditor"
          :address="address"
          :can-edit="canEditAddress"
          :customer-infoauto-id="sectionInfoautoId"
          :customer-name="sectionCustomerName"
          :saving="saving"
          @save="saveChanges"
          @discard="handleDiscard"
          @cancel="handleCancel"
          @manage-infoauto="showAddressPhoneModal = true"
          @select-address="showWebAddressSelector = true"
          @select-phone="showWebPhoneSelector = true"
          @edit-infoauto-address="editSelectedInfoautoAddress"
        ></AddressEditorForm>
        <SaveDefaultButton
          v-if="addressSection !== 'forwarder-address' && canSaveSectionDefault"
          :disabled="saving"
          @click="saveDefault(sectionDefaultField)"
        >
          {{ $t("message.order.save_address_default") }}
        </SaveDefaultButton>
      </div>
    </div>
  </div>
</template>

<script>
import { updateOrder } from "@/api/store/OrdersService.js";
import { infoautoGetSingleCustomer } from "@/api/store/Infoauto.js";
import InfoautoCustomerFinder from "../customer/InfoautoCustomerFinder.vue";
import InfoautoAddressForm from "../Infoauto/InfoautoAddressForm.vue";
import InfoautoAddressFormCostaRica from "../Infoauto/InfoautoAddressFormCostaRica.vue";
import InfoautoPhoneForm from "../Infoauto/InfoautoPhoneForm.vue";
import InfoautoCustomerAddressPhonesModal from "../customer/InfoautoCustomerAddressPhonesModal.vue";
import InfoautoEditAddressModal from "../customer/InfoautoEditAddressModal.vue";
import InfoautoEditAddressModalCostaRica from "../customer/InfoautoEditAddressModalCostaRica.vue";
import WebAddressPhoneSelectorModal from "../customer/WebAddressPhoneSelectorModal.vue";
import AddressEditorForm from "./AddressEditorForm.vue";
import ForwarderManager from "./ForwarderManager.vue";
import SaveDefaultButton from "./SaveDefaultButton.vue";
import { saveCustomerOrderDefault } from "@/api/store/CustomersService.js";

export default {
  name: "AddressManager",
  components: {
    InfoautoCustomerFinder,
    InfoautoAddressForm,
    InfoautoAddressFormCostaRica,
    InfoautoPhoneForm,
    InfoautoCustomerAddressPhonesModal,
    InfoautoEditAddressModal,
    InfoautoEditAddressModalCostaRica,
    WebAddressPhoneSelectorModal,
    AddressEditorForm,
    ForwarderManager,
    SaveDefaultButton
  },
  props: {
    address: {
      validator: prop => typeof prop === "object" || prop === null,
      required: true
    },
    customer: {
      validator: prop => typeof prop === "object" || prop === null,
      required: true
    },
    customerInfoauto: {
      validator: prop =>
        typeof prop === "object" || prop === null || prop === "undefined",
      required: true
    },
    orderNumber: {
      type: String,
      required: true
    },
    editing: {
      type: String,
      required: true
    },
    canBeEdited: {
      type: Boolean,
      required: true
    },
    addressSection: {
      type: String,
      required: true
    },
    sameShippingAndBilling: {
      type: Boolean,
      required: false,
      default: false
    },
    // Forwarder-specific props
    orderFull: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      saving: false,
      loading: false,
      showInfoautoSearch: false,
      showNewInfoautoAddressForm: false,
      showNewInfoautoPhoneForm: false,
      showAddressPhoneModal: false,
      showEditInfoautoAddressModal: false,
      showWebAddressSelector: false,
      showWebPhoneSelector: false,
      selectedInfoautoAddress: null,
      // Who receives when it is not the customer (orderFull.shippingInfoautoId)
      showReceiverSearch: false,
      receiverInfoautoFull: null,

      // Forwarder-specific data
      currentInfoautoCustomerId: null,
      customerInfoautoFull: null,
      forwarderAddress: null,
      forwarderPhone: null
    };
  },
  computed: {
    isCostaRicaStore() {
      const activeStore = this.$store.getters["general/getActiveStore"];
      return activeStore?.code === "nkr_costa_rica";
    },
    canEditAddress() {
      if (!this.canBeEdited) {
        return false;
      }
      if (
        this.addressSection === "billing-address" &&
        this.sameShippingAndBilling
      ) {
        return false;
      }
      return true;
    },
    changesPending() {
      if (this.addressSection === "forwarder-address") {
        return this.$refs.forwarderManager?.hasChanges ?? false;
      }
      return this.$refs.addressEditor?.hasChanges ?? false;
    },
    customerDisplayName() {
      return this.infoautoDisplayName(this.customerInfoauto);
    },
    // ===== Who receives =====
    shippingInfoautoId() {
      return this.orderFull?.shippingInfoautoId || null;
    },
    receiverName() {
      return this.infoautoDisplayName(this.receiverInfoautoFull);
    },
    // The shipping address is then one of the receiver's
    isShippingToReceiver() {
      return (
        this.addressSection === "shipping-address" && !!this.shippingInfoautoId
      );
    },
    // Infoauto person whose addresses and phones this section uses
    sectionInfoauto() {
      return this.isShippingToReceiver
        ? this.receiverInfoautoFull
        : this.customerInfoauto;
    },
    sectionInfoautoId() {
      return this.isShippingToReceiver
        ? this.shippingInfoautoId
        : this.customerInfoauto?.generalInfo?.infoautoId;
    },
    sectionCustomerName() {
      return this.isShippingToReceiver
        ? this.receiverName
        : this.customerDisplayName;
    },
    forwarderCustomerName() {
      return this.customerInfoautoFull?.generalInfo?.name || "";
    },
    forwarderAddressOptions() {
      if (!this.customerInfoautoFull?.address) return [];
      return this.customerInfoautoFull.address.filter(
        addr => addr.disabled !== 1
      );
    },
    forwarderPhoneOptions() {
      if (!this.customerInfoautoFull?.phone) return [];
      return this.customerInfoautoFull.phone
        .filter(phone => phone.disabled !== 1)
        .map(phone => ({
          areaCode: phone.areaCode || "",
          number: phone.number,
          infoautoPhoneId: phone.infoautoPhoneId || ""
        }));
    },
    // ===== Defaults (infoauto_customer_settings) =====
    sectionDefaultField() {
      return this.addressSection === "billing-address"
        ? "billing_address"
        : "shipping_address";
    },
    // Effective defaults of the customer: saved and active, otherwise the
    // first active (billing: otherwise the shipping ones)
    canSaveSectionDefault() {
      const defaults = this.orderFull?.customerDefaults;
      if (!this.canBeEdited || !defaults || !this.address?.infoautoAddressId) {
        return false;
      }
      // The receiver's address is not one of the customer's
      if (this.isShippingToReceiver) {
        return false;
      }
      const prefix =
        this.addressSection === "billing-address" ? "billing" : "shipping";
      return !this.sameInfoautoIds(
        this.address.infoautoAddressId,
        this.address.infoautoPhoneId,
        defaults[`${prefix}AddressId`],
        defaults[`${prefix}PhoneId`]
      );
    },
    // The forwarder's own default (its shipping one)
    canSaveForwarderAddressDefault() {
      const defaults = this.customerInfoautoFull?.orderDefaults;
      if (!this.canBeEdited || !defaults) return false;
      if (!this.forwarderAddress?.infoautoAddressId) return false;
      return !this.sameInfoautoIds(
        this.forwarderAddress.infoautoAddressId,
        this.forwarderPhone?.infoautoPhoneId,
        defaults.shippingAddressId,
        defaults.shippingPhoneId
      );
    },
    canSaveCustomerForwarder() {
      const defaults = this.orderFull?.customerDefaults;
      return (
        this.canBeEdited &&
        !!defaults &&
        !!this.orderFull.forwarderInfoautoId &&
        this.orderFull.forwarderInfoautoId !== defaults.forwarderInfoautoId
      );
    }
  },
  watch: {
    addressSection: {
      handler(val) {
        if (val === "forwarder-address") {
          this.initForwarderData();
        }
      },
      immediate: true
    },
    // The order is replaced after every save (e.g. the forwarder's default
    // address the API puts when choosing it)
    orderFull() {
      this.initForwarderData();
    },
    shippingInfoautoId: {
      handler(id) {
        this.receiverInfoautoFull = null;
        if (id) {
          this.loadReceiver();
        }
      },
      immediate: true
    }
  },
  methods: {
    infoautoDisplayName(infoautoCustomer) {
      if (!infoautoCustomer) return "";
      if (infoautoCustomer.isPerson === 1) {
        return `${infoautoCustomer.generalInfo.name} ${infoautoCustomer.generalInfo.surname}`;
      }
      return infoautoCustomer.generalInfo.name;
    },
    // ===== Who receives =====
    loadReceiver() {
      const id = this.shippingInfoautoId;
      infoautoGetSingleCustomer(id).then(response => {
        // The receiver may have changed meanwhile
        if (id === this.shippingInfoautoId) {
          this.receiverInfoautoFull = response;
        }
      });
    },
    selectedReceiver(customer) {
      this.showReceiverSearch = false;
      this.saveReceiver(customer.personaId);
    },
    // null: the customer receives. The API puts the receiver's default
    // address and phone as the shipping ones
    saveReceiver(infoautoCustomerId) {
      if (this.saving) return;
      this.saving = true;
      this.$emit("updating");
      updateOrder(this.orderFull.orderNumber, "shipping-customer", {
        infoautoCustomerId
      })
        .then(res => {
          (res?.warnings || []).forEach(message => {
            this.$toast.open({ message, type: "info", position: "top-right" });
          });
          this.$emit("finishedEditing", true);
        })
        .catch(e => {
          this.$toast.open({
            message: e.response?.data?.message || e.message,
            type: "error",
            position: "top-right"
          });
          this.$emit("finishedEditing", false);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    // Addresses or phones may have been created or edited in it
    closeAddressPhoneModal() {
      this.showAddressPhoneModal = false;
      if (this.isShippingToReceiver) {
        this.loadReceiver();
      }
    },
    // ===== Forwarder Methods =====
    initForwarderData() {
      if (this.addressSection === "forwarder-address" && this.orderFull) {
        const forwarderId = this.orderFull.forwarderInfoautoId;
        // The customer only reloads when the forwarder changes
        if (
          forwarderId &&
          (forwarderId !== this.currentInfoautoCustomerId ||
            !this.customerInfoautoFull)
        ) {
          this.loadForwarderCustomer(forwarderId);
        }
        if (!forwarderId) {
          this.customerInfoautoFull = null;
        }
        this.currentInfoautoCustomerId = forwarderId;
        this.forwarderAddress = null;
        this.forwarderPhone = null;
        if (this.orderFull.forwarderAddress) {
          this.forwarderAddress = {
            infoautoAddressId:
              this.orderFull.forwarderAddress.infoautoAddressId || "",
            name: this.orderFull.forwarderAddress.name,
            company: this.orderFull.forwarderAddress.company,
            street1: this.orderFull.forwarderAddress.street1,
            street2: this.orderFull.forwarderAddress.street2,
            street3: this.orderFull.forwarderAddress.street3,
            city: this.orderFull.forwarderAddress.city,
            state: this.orderFull.forwarderAddress.state,
            country: this.orderFull.forwarderAddress.country,
            postalCode: this.orderFull.forwarderAddress.postalCode,
            residential: this.orderFull.forwarderAddress.residential
          };
          // Without a number there is no phone, even if the row exists
          this.forwarderPhone = this.orderFull.forwarderAddress.phone
            ? {
                areaCode: this.orderFull.forwarderAddress.phoneAreaCode || "",
                number: this.orderFull.forwarderAddress.phone,
                infoautoPhoneId:
                  this.orderFull.forwarderAddress.infoautoPhoneId || ""
              }
            : null;
        }
      }
    },
    loadForwarderCustomer(customerId) {
      this.loading = true;
      infoautoGetSingleCustomer(customerId)
        .then(response => {
          this.customerInfoautoFull = response;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    selectedInfoautoCustomer(customer) {
      this.loading = true;
      this.showInfoautoSearch = false;
      // The API puts the forwarder's default address and phone (saved and
      // active, otherwise the first active)
      updateOrder(this.orderFull.orderNumber, "forwarder", {
        infoautoCustomerId: customer.personaId,
        addressId: null,
        address: null,
        useDefaultAddress: true
      })
        .then(res => {
          (res?.warnings || []).forEach(message => {
            this.$toast.open({ message, type: "info", position: "top-right" });
          });
          this.$emit("finishedEditing", true);
        })
        .catch(e => {
          this.$toast.open({
            message: e.response?.data?.message || e.message,
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    sameCustomerInOrder() {
      if (this.orderFull?.customerInfoauto?.generalInfo) {
        this.selectedInfoautoCustomer({
          personaId: this.orderFull.customerInfoauto.generalInfo.infoautoId
        });
      }
    },
    removeForwarder() {
      this.loading = true;
      updateOrder(this.orderFull.orderNumber, "forwarder", {
        infoautoCustomerId: null,
        addressId: null,
        address: null
      })
        .then(() => {
          this.currentInfoautoCustomerId = null;
          this.customerInfoautoFull = null;
          this.forwarderAddress = null;
          this.forwarderPhone = null;
          this.$emit("finishedEditing", true);
        })
        .catch(e => {
          this.$toast.open({
            message: e.response?.data?.message || e.message,
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleForwarderAddressCreated(address) {
      this.loadForwarderCustomer(this.currentInfoautoCustomerId);
      const newAddress = {
        infoautoAddressId: address.infoautoAddressId || "",
        name: address.name,
        company: address.company,
        street1: address.street1,
        street2: address.street2,
        street3: address.street3,
        city: address.city,
        state: address.state,
        country: address.country,
        postalCode: address.postalCode,
        residential: address.residential
      };
      this.$refs.forwarderManager?.setAddress(newAddress);
      this.showNewInfoautoAddressForm = false;
    },
    handleForwarderPhoneCreated(phoneLabel, phone) {
      this.loadForwarderCustomer(this.currentInfoautoCustomerId);
      this.$refs.forwarderManager?.setPhone(phone);
      this.showNewInfoautoPhoneForm = false;
    },
    saveForwarderChanges(data) {
      if (this.loading) return;
      this.loading = true;
      this.saving = true;
      const orderUpdates = {
        address: data.address,
        phone: data.phone,
        phoneAreaCode: data.phoneAreaCode,
        infoautoPhoneId: data.infoautoPhoneId,
        infoautoCustomerId: this.currentInfoautoCustomerId
      };
      updateOrder(this.orderFull.orderNumber, "forwarder", orderUpdates)
        .then(() => {
          this.$emit("finishedEditing", true);
        })
        .catch(e => {
          this.$toast.open({
            message: e.response.data.message,
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.loading = false;
          this.saving = false;
        });
    },

    // An order without phone saves a default without phone, and the API
    // resolves that to the first active one (pickInfoautoCustomerItem):
    // comparing it left the save button up forever after saving
    sameInfoautoIds(addressId, phoneId, defaultAddressId, defaultPhoneId) {
      if (String(addressId || "") !== String(defaultAddressId || "")) {
        return false;
      }
      return !phoneId || String(phoneId) === String(defaultPhoneId || "");
    },
    // Saves the order's persisted value as the default (see
    // Customers::order_default_post): the customer's, or the forwarder's
    // own for forwarder_address
    async saveDefault(field) {
      if (this.saving) return;
      this.saving = true;
      try {
        await saveCustomerOrderDefault(this.orderFull.orderNumber, field);
        this.$toast.open({
          message: this.$t("message.order.default_saved"),
          type: "success",
          position: "top-right"
        });
        if (field === "forwarder_address") {
          this.loadForwarderCustomer(this.currentInfoautoCustomerId);
        }
        this.$emit("finishedEditing", true);
      } catch (e) {
        this.$toast.open({
          message: e.response?.data?.message || "Error saving default",
          type: "error",
          position: "top-right"
        });
      } finally {
        this.saving = false;
      }
    },

    // ===== Shipping/Billing Address Methods =====
    handleInfoautoAddressSelected(address) {
      console.log("address selected", address);
      // Map infoauto address to standard format
      const mappedAddress = this.mapInfoautoAddress(address);
      // Infoauto addresses usually have no phone of their own: keep the
      // order's one instead of leaving the order without phone
      const current = this.$refs.addressEditor?.currentAddress;
      if (!mappedAddress.phone && !mappedAddress.infoautoPhoneId && current) {
        mappedAddress.phone = current.phone || "";
        mappedAddress.phoneAreaCode = current.phoneAreaCode || "";
        mappedAddress.infoautoPhoneId = current.infoautoPhoneId || "";
      }
      this.$refs.addressEditor?.selectAddress(mappedAddress);
      this.showAddressPhoneModal = false;
    },
    handleInfoautoPhoneSelected(phone) {
      this.$refs.addressEditor?.updatePhone(
        phone.number,
        phone.infoautoPhoneId || "",
        phone.areaCode || ""
      );
      this.showAddressPhoneModal = false;

      // Trigger save after phone update
      const currentAddress = this.$refs.addressEditor?.currentAddress;
      if (currentAddress) {
        this.saveChanges(currentAddress);
      }
    },
    handleWebAddressSelected(address) {
      const mappedAddress = this.mapWebAddress(address);
      this.$refs.addressEditor?.selectAddress(mappedAddress);
      this.showWebAddressSelector = false;
    },
    handleWebPhoneSelected(phone) {
      this.$refs.addressEditor?.updatePhone(
        phone.number,
        phone.infoautoPhoneId || "",
        phone.areaCode || ""
      );
      this.showWebPhoneSelector = false;

      // Trigger save after phone update
      const currentAddress = this.$refs.addressEditor?.currentAddress;
      if (currentAddress) {
        this.saveChanges(currentAddress);
      }
    },
    editSelectedInfoautoAddress() {
      const editor = this.$refs.addressEditor;
      const address = editor?.currentAddress;
      if (!address?.infoautoAddressId) return;

      const fullAddress = this.sectionInfoauto?.address?.find(
        addr => addr.infoautoAddressId === address.infoautoAddressId
      );
      if (fullAddress) {
        this.selectedInfoautoAddress = fullAddress;
        this.showEditInfoautoAddressModal = true;
      }
    },
    handleInfoautoAddressUpdated() {
      this.showEditInfoautoAddressModal = false;
      this.selectedInfoautoAddress = null;
      if (this.isShippingToReceiver) {
        this.loadReceiver();
      }
      this.$emit("finishedEditing", true);
    },
    saveChanges(address) {
      this.saving = true;
      this.$emit("updating");
      this.$emit("startEditing", this.addressSection);

      let updateType = "shipping-address";
      if (this.addressSection === "billing-address") {
        updateType = "billing-address";
      }

      updateOrder(this.orderNumber, updateType, address)
        .then(() => {
          this.$emit("finishedEditing", true);
        })
        .catch(e => {
          this.$toast.open({
            message: e.response?.data?.message || e.message,
            type: "error",
            position: "top-right"
          });
          // Clears the modal's "updating" (emitted above)
          this.$emit("finishedEditing", false);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    handleDiscard() {
      // Editor handles its own state, nothing to do here
    },
    handleCancel() {
      // User cancelled without saving
    },

    // ===== Utility Methods =====
    mapInfoautoAddress(address) {
      const defaultAddress = {
        name: "",
        company: "",
        street1: "",
        street2: "",
        street3: "",
        city: "",
        state: "",
        country: "US",
        phone: "",
        phoneAreaCode: "",
        postalCode: "",
        residential: null,
        addressVerified: null,
        infoautoAddressId: "",
        infoautoPhoneId: "",
        provincia: "",
        canton: "",
        distrito: "",
        barrio: "",
        codProvincia: "",
        codCanton: "",
        codDistrito: "",
        codBarrio: "",
        tipo3: 0
      };

      const baseAddress = {
        ...defaultAddress,
        name: address.name || "",
        company: address.company || "",
        street1: address.street1 || "",
        street2: address.street2 || "",
        street3: address.street3 || "",
        country: address.country || "",
        postalCode: address.postalCode || address.postalcode || "",
        residential: address.residential?.toString() || "0",
        phone: address.phone || "",
        phoneAreaCode: address.phoneAreaCode || "",
        infoautoAddressId:
          address.infoautoAddressId || address.infoautoaddressid || "",
        infoautoPhoneId: address.infoautoPhoneId || "",
        tipo3: address.tipo3 || 0
      };
      // Add Costa Rica specific fields if available
      if (this.isCostaRicaStore) {
        baseAddress.provincia = address.provincia || "";
        baseAddress.canton = address.canton || "";
        baseAddress.distrito = address.distrito || "";
        baseAddress.barrio = address.barrio || "";
        baseAddress.codProvincia =
          address.codProvincia || address.codprovincia || "";
        baseAddress.codCanton = address.codCanton || address.codcanton || "";
        baseAddress.codDistrito =
          address.codDistrito || address.coddistrito || "";
        baseAddress.codBarrio = address.codBarrio || address.codbarrio || "";
        baseAddress.country = address.countryCode || "CRI";
      } else {
        // Standard address fields
        baseAddress.city = address.city || "";
        baseAddress.state = address.state || "";
      }
      return baseAddress;
    },
    mapWebAddress(address) {
      const defaultAddress = {
        name: "",
        company: "",
        street1: "",
        street2: "",
        street3: "",
        city: "",
        state: "",
        country: "US",
        phone: "",
        phoneAreaCode: "",
        postalCode: "",
        residential: null,
        addressVerified: null,
        infoautoAddressId: "",
        infoautoPhoneId: "",
        provincia: "",
        canton: "",
        distrito: "",
        barrio: "",
        codProvincia: "",
        codCanton: "",
        codDistrito: "",
        codBarrio: "",
        tipo3: 0
      };

      return {
        ...defaultAddress,
        ...address,
        residential: address.residential?.toString() || "0",
        infoautoAddressId: address.infoautoAddressId || "",
        infoautoPhoneId: address.infoautoPhoneId || ""
      };
    }
  }
};
</script>

<style scoped>
/* .is-editing y .is-active en vez de .active: _base-modal.vue fuerza
   .active { display: block !important } de forma global */
.w3.is-editing {
  z-index: 20;
  position: relative;
  background: white;
}

.tab-nav {
  display: flex;
  gap: 2px;
  padding: 3px;
  background-color: #f3f4f6;
  border-radius: 10px;
  margin-bottom: 1rem;
}

.tab-nav .tab-button {
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 0.45rem 0.75rem;
  background-color: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s, box-shadow 0.15s;
  font-size: 16px;
  font-weight: 500;
  color: #4b5563;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tab-nav .tab-button:hover:not(:disabled):not(.is-active) {
  background-color: rgba(255, 255, 255, 0.6);
  color: #111827;
}

.tab-nav .tab-button.is-active,
.tab-nav .tab-button.is-active:hover {
  background-color: white;
  color: #126e6a;
  font-weight: 600;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
}

.tab-nav .tab-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.receiver-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem 0.5rem;
  margin-bottom: 0.75rem;
  padding: 0.5rem 0.75rem;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
}

.receiver-label {
  color: #6b7280;
}

.receiver-name {
  font-weight: 600;
  color: #111827;
}

.receiver-badge {
  padding: 0 0.4rem;
  border-radius: 9999px;
  background-color: #fef3c7;
  color: #92400e;
  font-size: 12px;
}

.receiver-row .text-link {
  color: #126e6a;
  font-weight: 500;
}

.receiver-row .text-link:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
